import React from 'react';
import { AlertTriangle, RefreshCw, ArrowLeft, FileText } from 'lucide-react';

/**
 * ItineraryErrorBoundary Component
 * Specialized error boundary for itinerary display with recovery options
 */
class ItineraryErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { 
      hasError: false, 
      error: null, 
      errorInfo: null,
      recoveryAttempts: 0 
    };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error('Itinerary Error Boundary caught an error:', error, errorInfo);
    this.setState({ errorInfo });
    
    // Log error for debugging
    if (process.env.NODE_ENV === 'development') {
      console.group('Itinerary Error Details');
      console.error('Error:', error);
      console.error('Error Info:', errorInfo);
      console.error('Component Stack:', errorInfo.componentStack);
      console.groupEnd();
    }
  }

  handleRetry = () => {
    this.setState(prevState => ({
      hasError: false,
      error: null,
      errorInfo: null,
      recoveryAttempts: prevState.recoveryAttempts + 1
    }));
  };

  handleGoBack = () => {
    if (this.props.onGoBack) {
      this.props.onGoBack();
    } else {
      window.history.back();
    }
  };

  handleRefresh = () => {
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      const { error, recoveryAttempts } = this.state;
      const maxAttempts = 3;

      return (
        <div className="flex justify-center items-center p-4 min-h-screen bg-gradient-to-br from-gray-50 to-blue-50">
          <div className="p-6 w-full max-w-md bg-white rounded-xl shadow-lg border border-gray-100">
            <div className="text-center">
              <div className="flex justify-center items-center mx-auto mb-4 w-16 h-16 bg-red-100 rounded-full">
                <AlertTriangle className="w-8 h-8 text-red-600" />
              </div>
              
              <h2 className="mb-2 text-xl font-bold text-gray-900">
                Itinerary Display Error
              </h2>
              
              <p className="mb-6 text-gray-600">
                {recoveryAttempts >= maxAttempts 
                  ? "We're having trouble displaying your itinerary. Please try refreshing the page or contact support."
                  : "Something went wrong while displaying your itinerary. Let's try to fix this."
                }
              </p>

              {process.env.NODE_ENV === 'development' && error && (
                <details className="mb-4 text-left">
                  <summary className="text-sm font-medium text-gray-700 cursor-pointer hover:text-gray-900">
                    Error Details (Development)
                  </summary>
                  <div className="overflow-auto p-3 mt-2 max-h-32 font-mono text-xs text-gray-800 bg-gray-100 rounded">
                    {error.toString()}
                  </div>
                </details>
              )}

              <div className="space-y-3">
                {recoveryAttempts < maxAttempts && (
                  <button
                    onClick={this.handleRetry}
                    className="flex gap-2 justify-center items-center px-4 py-2 w-full text-white bg-blue-600 rounded-md transition-colors hover:bg-blue-700"
                  >
                    <RefreshCw className="w-4 h-4" />
                    Try Again ({recoveryAttempts + 1}/{maxAttempts})
                  </button>
                )}

                <button
                  onClick={this.handleGoBack}
                  className="flex gap-2 justify-center items-center px-4 py-2 w-full text-white bg-gray-600 rounded-md transition-colors hover:bg-gray-700"
                >
                  <ArrowLeft className="w-4 h-4" />
                  Go Back
                </button>

                <button
                  onClick={this.handleRefresh}
                  className="flex gap-2 justify-center items-center px-4 py-2 w-full text-white bg-blue-500 rounded-md transition-colors hover:bg-blue-600"
                >
                  <RefreshCw className="w-4 h-4" />
                  Refresh Page
                </button>

                {this.props.onDownloadPDF && (
                  <button
                    onClick={this.props.onDownloadPDF}
                    className="flex gap-2 justify-center items-center px-4 py-2 w-full text-white bg-blue-700 rounded-md transition-colors hover:bg-blue-800"
                  >
                    <FileText className="w-4 h-4" />
                    Download PDF
                  </button>
                )}
              </div>

              {recoveryAttempts >= maxAttempts && (
                <div className="p-3 mt-4 bg-blue-50 rounded-md border border-blue-200">
                  <p className="text-sm text-blue-800">
                    If the problem persists, please contact support with the error details above.
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ItineraryErrorBoundary; 